'use client'

import { type KeyboardEvent, memo } from 'react'
import { Badge, Box, HStack, Text, VStack } from '@chakra-ui/react'
import type { MapLocatorTag } from 'ecosystem'
import type { MapLocatorEntry } from './useMapLocator'
import { MAP_LOCATOR_CN, mapLocatorCn as cn } from './constants'
import { formatDistance, fullAddress } from './utils'

interface ResultsListProps {
  entries: MapLocatorEntry[]
  tags: MapLocatorTag[]
  activeItemId: string | null
  onSelect: (item: MapLocatorEntry) => void
  showDistance: boolean
  labels: {
    noResults: string
    distanceUnit: string
  }
}

/** Also the accessible fallback for the marker set — keyboard reachable, screen-reader friendly. */
const ResultsList = ({
  entries,
  tags,
  activeItemId,
  onSelect,
  showDistance,
  labels
}: ResultsListProps) => {
  if (!entries.length) {
    return (
      <Box className={cn(MAP_LOCATOR_CN.LIST)} p={4}>
        <Text color="gray.600">{labels.noResults}</Text>
      </Box>
    )
  }

  return (
    <VStack
      align="stretch"
      as="ul"
      className={cn(MAP_LOCATOR_CN.LIST)}
      h="full"
      listStyleType="none"
      overflowY="auto"
      spacing={0}
    >
      {entries.map((entry) => {
        const isActive = entry.id === activeItemId
        const address = fullAddress(entry)
        const entryTags = (entry.tagIds || [])
          .map((id) => tags.find((tag) => tag.id === id))
          .filter(Boolean) as MapLocatorTag[]

        return (
          <Box
            aria-current={isActive || undefined}
            as="li"
            bg={isActive ? 'blackAlpha.50' : undefined}
            borderBottomWidth="1px"
            className={cn(MAP_LOCATOR_CN.LIST_ITEM)}
            cursor="pointer"
            key={entry.id}
            onClick={() => onSelect(entry)}
            onKeyDown={(event: KeyboardEvent<HTMLElement>) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onSelect(entry)
              }
            }}
            px={4}
            py={3}
            role="button"
            tabIndex={0}
            _hover={{ bg: 'blackAlpha.50' }}
          >
            <HStack align="start" justify="space-between" spacing={3}>
              <Box>
                <Text fontWeight="semibold">{entry.name}</Text>

                {address ? (
                  <Text color="gray.600" fontSize="sm">
                    {address}
                  </Text>
                ) : null}

                {entryTags.length ? (
                  <HStack mt={1} spacing={1} wrap="wrap">
                    {entryTags.map((tag) => (
                      <Badge
                        bg={tag.color || undefined}
                        color={tag.color ? 'white' : undefined}
                        fontSize="xx-small"
                        key={tag.id}
                        rounded="full"
                      >
                        {tag.label}
                      </Badge>
                    ))}
                  </HStack>
                ) : null}
              </Box>

              {showDistance && entry.distanceKm !== undefined ? (
                <Text color="gray.500" fontSize="sm" whiteSpace="nowrap">
                  {formatDistance(entry.distanceKm, labels.distanceUnit)}
                </Text>
              ) : null}
            </HStack>
          </Box>
        )
      })}
    </VStack>
  )
}

/* Memoised so a keystroke, which only changes state upstream, does not walk
 * this subtree before React has painted the character. */
export default memo(ResultsList)
